/**
 * List of racks that have been placed on the warehouse layout.
 * Each rack shows selection state, anchor indicator, and action buttons.
 */

import React from 'react';
import { RotateCcw, Trash2 } from 'lucide-react';
import { useLayoutEditor } from '../contexts/LayoutEditorContext';
import { useTranslation } from '../../../../../contexts/I18nProvider';

const PlacedRacksList: React.FC = () => {
  const { state, dispatch, rackDimensionsMap } = useLayoutEditor();
  const { t } = useTranslation();
  const { layout, editorState } = state;

  if (!layout) return null;

  return (
    <div className="p-3">
      <div className="space-y-1">
        {layout.racks.map(rack => {
          const dim = rackDimensionsMap.get(rack.rackNumber);
          const isSelected = editorState.selectedRacks.has(rack.rackNumber);
          const isAnchor = editorState.anchorRack === rack.rackNumber;

          return (
            <div
              key={rack.rackNumber}
              className={`flex items-center justify-between p-2 rounded-lg cursor-pointer transition-colors ${
                isAnchor
                  ? 'bg-green-100 border-2 border-green-400'
                  : isSelected
                  ? 'bg-blue-100 border-2 border-blue-400'
                  : 'bg-gray-50 hover:bg-gray-100'
              }`}
              onClick={() => dispatch({ type: 'SET_SELECTION', selectedRacks: new Set([rack.rackNumber]) })}
            >
              <div>
                <div className="font-medium text-sm flex items-center gap-1">
                  {isAnchor && <span className="text-green-600">&#9875;</span>}
                  {rack.rackNumber}
                </div>
                <div className="text-xs text-gray-500">
                  {dim ? `${dim.verticals}\u00d7${dim.shelves}` : ''} &bull; {rack.rotation}&deg;
                </div>
              </div>
              <div className="flex gap-1">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    dispatch({
                      type: 'SET_ANCHOR',
                      rackNumber: isAnchor ? null : rack.rackNumber,
                    });
                  }}
                  className={`p-1 rounded ${isAnchor ? 'bg-green-200' : 'hover:bg-gray-200'}`}
                  title={isAnchor ? t('manager.modules.warehouseLayout.placedRacks.removeAsAnchor', {}, 'Fjern som anker') : t('manager.modules.warehouseLayout.placedRacks.setAsAnchor', {}, 'Sett som anker')}
                >
                  <span className="text-xs">&#9875;</span>
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    dispatch({ type: 'ROTATE_RACK', rackNumber: rack.rackNumber });
                  }}
                  className="p-1 hover:bg-gray-200 rounded"
                  title={t('manager.modules.warehouseLayout.placedRacks.rotate', {}, 'Roter')}
                >
                  <RotateCcw className="w-4 h-4" />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    dispatch({ type: 'REMOVE_RACK', rackNumber: rack.rackNumber });
                  }}
                  className="p-1 hover:bg-red-100 text-red-600 rounded"
                  title={t('common.buttons.remove', {}, 'Fjern')}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PlacedRacksList;
